// ============================================
// Chat Messaging - Renderer (ES6)
// 메시지 표시 및 렌더링
// ============================================

import { getCurrentSessionId } from '../../app/state.js';
import { formatAIMessage } from '../parser.js';
import { formatUserMessage } from '../send.js';
import { setupMessageLongPress } from '../context-menu.js';
import { showImageModal } from '../../image.js';
import { createLazyImage } from '../../shared/lazy-image.js';

// ============================================
// 메시지 초기화
// ============================================

export function clearMessages() {
  const container = document.getElementById('messagesContainer');
  if (!container) return;

  // 스크롤 위치 먼저 초기화 (이전 채팅 스크롤 위치가 보이는 문제 방지)
  container.scrollTop = 0;
  container.innerHTML = `
    <div class="empty-state">
      <h3>새로운 대화를 시작하세요</h3>
      <p>AI가 대화 내용을 기억합니다</p>
    </div>
  `;

  // 채팅이 선택되지 않은 상태에서는 프로필 뱃지 숨김
  if (!getCurrentSessionId()) {
    const profileBadge = document.getElementById('currentProfileBadge');
    if (profileBadge) profileBadge.style.display = 'none';
  }
}

// ============================================
// 메시지 추가
// ============================================

export function appendMessage(role, content, timestamp, messageId = null, turn = null) {
  appendMessageWithoutScroll(role, content, timestamp, messageId, false, turn);

  const container = document.getElementById('messagesContainer');
  if (container) {
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  }
}

// 스크롤 없이 메시지 추가 (로드 시 사용)
export function appendMessageWithoutScroll(role, content, timestamp, messageId = null, isIntro = false, turn = null) {
  const container = document.getElementById('messagesContainer');
  if (!container) return;

  // 메모리 마커 제거 (<<< 이후 모두 제거)
  if (content && content.includes('<<<')) {
    content = content.split('<<<')[0].trim();
  }

  // Remove empty state if exists
  const emptyState = container.querySelector('.empty-state');
  if (emptyState) emptyState.remove();

  const div = document.createElement('div');
  // 'model'과 'assistant' 모두 assistant로 표시
  const displayRole = (role === 'model') ? 'assistant' : role;
  div.className = `message ${displayRole}`;
  if (messageId) {
    div.dataset.messageId = messageId;
  }
  if (timestamp) {
    div.dataset.timestamp = timestamp.getTime();
  }
  if (turn) {
    div.dataset.turn = turn;
  }

  let formattedContent;
  if (role === 'assistant' || role === 'model') {
    formattedContent = formatAIMessage(content);
    // user-quote는 AI 메시지 안에 원래 위치에 표시됨 (parser.js에서 처리)
    // 길게 누르기로 컨텍스트 메뉴 표시
    setupMessageLongPress(div, content);
  } else {
    formattedContent = formatUserMessage(content);
    // 유저 메시지도 길게 누르면 복사 가능
    setupMessageLongPress(div, content);
  }

  // AI 메시지에 액션 버튼 (비활성화됨)
  const actionsHtml = '';

  div.innerHTML = `
    <div class="message-content">${formattedContent}</div>
    ${actionsHtml}
  `;

  // AI 메시지는 원본 콘텐츠 저장 (편집용)
  if (role === 'assistant' || role === 'model') {
    div.dataset.rawContent = content;
  }

  container.appendChild(div);
}

// ============================================
// Scene Image 추가 (저장된 장면 이미지용)
// Skeleton UI + 비동기 로드 적용
// ============================================

export function appendSceneImage(imageUrl, timestamp, fullImageUrl = null) {
  const container = document.getElementById('messagesContainer');
  if (!container || !imageUrl) return;

  // Remove empty state if exists
  const emptyState = container.querySelector('.empty-state');
  if (emptyState) emptyState.remove();

  const div = document.createElement('div');
  div.className = 'message assistant scene-image';
  if (timestamp) {
    div.dataset.timestamp = timestamp.getTime();
  }

  // 모달에서는 원본 이미지 표시 (fullImageUrl이 있으면 사용)
  const modalUrl = fullImageUrl || imageUrl;

  // Skeleton UI + 비동기 로드 적용
  const imageWrapper = createLazyImage(imageUrl, '생성된 이미지', 'scene-image-skeleton', {
    onClick: () => showImageModal(modalUrl, '생성된 이미지')
  });

  // scene-image-container로 감싸기
  const sceneContainer = document.createElement('div');
  sceneContainer.className = 'scene-image-container';
  sceneContainer.dataset.url = imageUrl;
  sceneContainer.appendChild(imageWrapper);

  // 확대 아이콘 오버레이 (SVG를 DOM API로 생성)
  const overlay = document.createElement('div');
  overlay.className = 'scene-image-overlay';
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7');
  svg.appendChild(path);
  overlay.appendChild(svg);
  sceneContainer.appendChild(overlay);

  div.appendChild(sceneContainer);
  container.appendChild(div);
}

